import React from 'react';
import dayjs from 'dayjs';
import { Button, Spin, Tooltip } from 'antd';
import { Pencil, X, CalendarX2 } from 'lucide-react';
import { StatusTag } from '../page';
import CalendarStoresModal from './CalendarStoresModal';

// Working week shown by the calendar; `dayNumber` matches the calendar's `day` (and dayjs().day()).
const DAYS = [
    { day: 'Lunes', dayNumber: '1' },
    { day: 'Martes', dayNumber: '2' },
    { day: 'Miercoles', dayNumber: '3' },
    { day: 'Jueves', dayNumber: '4' },
    { day: 'Viernes', dayNumber: '5' }
];

export default function Calendar({ calendars, promotor, allStores, createCalendar, isOpen, setIsOpen, isUpdating = false, readonly = false }) {
    const [stores, setStores] = React.useState([]);
    const [day, setDay] = React.useState('');
    const today = String(dayjs().day());

    const calendarStores = (day) => {
        if (calendars.length <= 0) return [];

        if (calendars?.filter((calendar) => calendar?.day === day)[0]?.stores?.length > 0) {
            return calendars?.filter((calendar) => calendar?.day === day)[0]?.stores;
        } else {
            return [];
        }
    };

    return (
        <div className="w-full">
            {isUpdating && (
                <div className="mb-3 flex items-center justify-end gap-2 text-xs text-surface-500" role="status">
                    <Spin size="small" />
                    Actualizando calendario...
                </div>
            )}
            <div className="w-full overflow-x-auto pb-1">
                <div className="grid grid-cols-5 gap-3 min-w-[1040px]">
                    {DAYS.map(({ day: dayName, dayNumber }) => (
                        <CalendarDay
                            key={dayNumber}
                            day={dayName}
                            dayNumber={dayNumber}
                            isToday={today === dayNumber}
                            stores={calendarStores(dayNumber)}
                            setIsOpen={setIsOpen}
                            setStores={setStores}
                            setDay={setDay}
                            userId={promotor?._id}
                            createCalendar={createCalendar}
                            isUpdating={isUpdating}
                            readonly={readonly}
                        />
                    ))}
                </div>
            </div>
            <CalendarStoresModal
                day={day}
                isOpen={isOpen}
                stores={allStores}
                user={promotor?._id}
                setIsOpen={setIsOpen}
                calendarStores={stores}
                createCalendar={createCalendar}
            />
        </div>
    );
}

const CalendarDay = ({ stores: dayStores, day, isToday, setIsOpen, setStores, setDay, dayNumber, userId, createCalendar, isUpdating, readonly }) => {
    const handleRemoveStore = (storeId) => {
        if (!userId || !storeId || isUpdating || readonly) return;
        const remainingStoreIds = (dayStores || []).filter((store) => store?._id !== storeId).map((store) => store?._id);
        createCalendar(userId, dayNumber, remainingStoreIds);
    };

    return (
        <div className={`flex flex-col min-w-0 rounded-xl border bg-surface-25 ${isToday ? 'border-primary-200' : 'border-surface-200'}`}>
            {/* Day header */}
            <div className={`flex items-center justify-between gap-2 px-3 h-11 border-b ${isToday ? 'border-primary-100' : 'border-surface-100'}`}>
                <div className="flex items-center gap-2 min-w-0">
                    <p className={`m-0 text-sm font-semibold truncate ${isToday ? 'text-primary-700' : 'text-surface-900'}`}>{day}</p>
                    {isToday && <StatusTag tone="brand">Hoy</StatusTag>}
                </div>
                <span className="text-xs font-medium text-surface-400 tabular-nums">{dayStores?.length || 0}</span>
            </div>

            {/* Stores */}
            <div className="flex flex-col gap-2 p-2.5 flex-1">
                {dayStores?.length === 0 && (
                    <div className="flex flex-col items-center justify-center gap-1.5 py-6 text-center text-xs text-surface-400">
                        <CalendarX2 className="text-[18px] text-surface-300" />
                        Sin pisos asignados
                    </div>
                )}
                {dayStores?.map((store) => (
                    <div key={store._id} className="rounded-lg border border-surface-200 bg-white p-3">
                        <div className="w-full flex flex-row justify-between items-start gap-2 mb-2">
                            <StatusTag tone="violet" className="uppercase text-[11px] tracking-wide max-w-full truncate">
                                {store.storeType}
                            </StatusTag>
                            {!readonly && (
                                <Tooltip title="Quitar piso del día">
                                    <Button
                                        type="text"
                                        size="small"
                                        danger
                                        icon={<X />}
                                        onClick={() => handleRemoveStore(store?._id)}
                                        disabled={isUpdating}
                                        aria-label="Quitar piso del día"
                                        className="-mr-1 -mt-0.5"
                                    />
                                </Tooltip>
                            )}
                        </div>
                        <div className="text-wrap">
                            <p className="sr-only">Nombre:</p>
                            <p className="m-0 text-[13px] font-medium leading-snug text-surface-900 break-words">{store.name}</p>
                        </div>
                        <div className="mt-1 flex flex-row items-center gap-x-1 text-xs text-surface-500">
                            <p className="m-0 font-medium">RIF:</p>
                            <p className="m-0 tabular-nums">{store.rif}</p>
                        </div>
                    </div>
                ))}
            </div>

            {!readonly && (
                <div className="p-2.5 pt-0">
                    <Button
                        block
                        icon={<Pencil />}
                        disabled={isUpdating}
                        onClick={() => {
                            if (isUpdating) return;
                            setStores(dayStores);
                            setDay(dayNumber);
                            setIsOpen(true);
                        }}>
                        Editar Pisos
                    </Button>
                </div>
            )}
        </div>
    );
};
